import { useState, useEffect } from 'react';
import { Plus, Trash2, Zap, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AdminLayout } from '@/components/AdminLayout';
import { flashSaleApi, productApi } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminFlashSales() {
  const [flashSales, setFlashSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    product_id: '',
    discount_percent: '',
    start_time: '',
    end_time: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [salesRes, productsRes] = await Promise.all([
        flashSaleApi.getAll(),
        productApi.getAll({ limit: 100 }),
      ]);
      setFlashSales(salesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.product_id || !formData.discount_percent || !formData.start_time || !formData.end_time) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await flashSaleApi.create({
        product_id: formData.product_id,
        discount_percent: parseFloat(formData.discount_percent),
        start_time: new Date(formData.start_time).toISOString(),
        end_time: new Date(formData.end_time).toISOString(),
      });
      toast.success('Flash sale created!');
      setDialogOpen(false);
      setFormData({ product_id: '', discount_percent: '', start_time: '', end_time: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create flash sale');
    }
  };

  const handleDelete = async (saleId) => {
    if (!window.confirm('Are you sure you want to delete this flash sale?')) return;

    try {
      await flashSaleApi.delete(saleId);
      toast.success('Flash sale deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete flash sale');
    }
  };

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Products not already in a flash sale
  const availableProducts = products.filter(
    (p) => !flashSales.some((s) => s.product_id === p.id)
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Flash Sales</h1>
            <p className="text-stone-400 mt-1">{flashSales.length} active/scheduled sales</p>
          </div>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-red-600 hover:bg-red-700"
            data-testid="add-flash-sale-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Flash Sale
          </Button>
        </div>

        {/* Flash Sales Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="flash-sales-grid">
          {loading ? (
            <p className="text-stone-500 col-span-full text-center py-8">Loading...</p>
          ) : flashSales.length === 0 ? (
            <div className="col-span-full text-center py-12 bg-stone-900 border border-stone-800">
              <Zap className="h-12 w-12 text-stone-600 mx-auto mb-4" />
              <p className="text-stone-500">No flash sales yet</p>
              <p className="text-sm text-stone-600 mt-1">Create one to boost your sales!</p>
            </div>
          ) : (
            flashSales.map((sale) => (
              <div
                key={sale.id}
                className={`bg-stone-900 border overflow-hidden ${
                  sale.is_active ? 'border-red-600' : 'border-stone-800'
                }`}
                data-testid={`flash-sale-${sale.id}`}
              >
                {sale.product && (
                  <div className="aspect-video bg-stone-800 relative">
                    {sale.product.images?.[0] ? (
                      <img
                        src={sale.product.images[0]}
                        alt={sale.product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Zap className="h-12 w-12 text-stone-700" />
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      {sale.is_active ? (
                        <Badge className="bg-red-600 text-white animate-pulse">LIVE</Badge>
                      ) : (
                        <Badge className="bg-stone-700 text-stone-300">Scheduled</Badge>
                      )}
                    </div>
                    <div className="absolute bottom-2 left-2">
                      <Badge className="bg-yellow-500 text-black font-bold">
                        -{sale.discount_percent}% OFF
                      </Badge>
                    </div>
                  </div>
                )}
                <div className="p-4">
                  <h3 className="font-semibold text-white">
                    {sale.product?.name || 'Unknown Product'}
                  </h3>
                  {sale.product && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-red-400 font-bold">
                        KES {(sale.product.price * (1 - sale.discount_percent / 100)).toLocaleString()}
                      </span>
                      <span className="text-sm text-stone-500 line-through">
                        KES {sale.product.price.toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="mt-4 pt-4 border-t border-stone-800 text-sm text-stone-400 space-y-1">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>Start: {formatDateTime(sale.start_time)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>End: {formatDateTime(sale.end_time)}</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-4 border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                    onClick={() => handleDelete(sale.id)}
                    data-testid={`delete-flash-sale-${sale.id}`}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    End Sale
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Create Flash Sale Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-stone-900 border-stone-800">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Zap className="h-5 w-5 text-red-400" />
                Create Flash Sale
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-stone-300">Select Product *</Label>
                <Select
                  value={formData.product_id}
                  onValueChange={(val) => setFormData({ ...formData, product_id: val })}
                >
                  <SelectTrigger className="mt-1 bg-stone-800 border-stone-700 text-white" data-testid="flash-sale-product-select">
                    <SelectValue placeholder="Choose a product" />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-700 max-h-60">
                    {availableProducts.map((product) => (
                      <SelectItem key={product.id} value={product.id} className="text-white">
                        {product.name} - KES {product.price.toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-stone-300">Discount Percentage *</Label>
                <Input
                  type="number"
                  value={formData.discount_percent}
                  onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                  className="mt-1 bg-stone-800 border-stone-700 text-white"
                  placeholder="e.g. 30"
                  min="1"
                  max="99"
                  data-testid="flash-sale-discount-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-stone-300">Start Time *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="mt-1 bg-stone-800 border-stone-700 text-white"
                    data-testid="flash-sale-start-input"
                  />
                </div>
                <div>
                  <Label className="text-stone-300">End Time *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="mt-1 bg-stone-800 border-stone-700 text-white"
                    data-testid="flash-sale-end-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-stone-700 text-stone-300"
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="save-flash-sale-btn">
                  Create Flash Sale
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
